import http from '../plugins/axios'

// ========== GraphQL Queries ==========

const queryGetInventories = (warehouseId = 0) => {
  const where = warehouseId > 0 ? `{
    column: WAREHOUSE_ID,
    operator: EQ,
    value: ${warehouseId}
  }` : ''
  return `query($page: Int, $first: Int!, $value: Mixed) {
    inventories(
      page: $page, 
      first: $first,
      where: {
        AND : [
        {
          column: PRODUCT_NAME,
          operator: LIKE,
          value: $value,
        },
        ${where}
      ]
    },
    orderBy: [{ column: CREATED_AT, order: DESC }]
    ) {
      data {
        id
        sku
        productName
        description
        specification {
          quantity
          quantityUnit
          weight
          weightUnit
          width
          length
          height
          dimensionUnit
        }
        categories {
          id
          name
          parent {
            id
            name
            parent {
              id
              name
            }
          }
        }
        warehouse {
          id
          name
        }
        user {
          id
          name
        }
        product {
          id
          title
          images {
            source
            is_default
          }
        }
        creator {
          id
          name
        }
        editor {
          id
          name
        }
        createdAt
        updatedAt
      }
      paginatorInfo {
        currentPage
        total
      }
    }
  }`
}

const queryGetInventory = `query($id: ID!) {
  inventory(id: $id) {
    id
    sku
    productName
    description
    warehouseId
    specification {
      quantity
      quantityUnit
      weight
      weightUnit
      width
      length
      height
      dimensionUnit
    }
    categories {
      id
      name
      parent {
        id
        name
        parent {
          id
          name
        }
      }
    }
    warehouse {
      id
      name
    }
    user {
      id
      name
    }
    product {
      id
      title
      images {
        source
        is_default
      }
    }
    creator {
      id
      name
    }
    editor {
      id
      name
    }
    createdAt
    updatedAt
  }
}`

const queryGetWarehouses = `query($first: Int!) {
  warehouses(first: $first) {
    data {
      id
      name
      address
      address2
      countryId
      provinceId
      cityId
      districtId
      ownedByAli
      isPrivate
      createdAt
      updatedAt
    }
  }
}`

const queryGetChildCategories = `query ($value: Mixed) {
  categories(first: 200, page: 1,where: {
    AND: [
      {
        column: PARENT_ID,
        operator: EQ,
        value: $value
      }
    ]
  }) {
    data {
      id
      name
    }
  }
}`

const queryGetMainCategories = `query {
  categories(first: 200, page: 1,where: {column: LEVEL, operator: EQ, value: 1}) {
    data {
      id
      name
    }
  }
}`

const queryGetUsers = `query($value: Mixed) {
  users(first: 20, page: 1, where: {column: NAME, operator: LIKE, value: $value}) {
    data {
      id
      name
      email
    }
  }
}`

const queryGetProducts = `query($value: Mixed) {
  products(first: 20, page: 1, where:{
    column: TITLE,
    operator: LIKE,
    value: $value
  }) {
    data {
      id
      title
      images {
        source
        is_default
      }
    }
  }
}`

const queryGetQuantityUnits = `query {
  quantityUnit {
    label
    name
    value
  }
}`

const queryGetDimensionUnits = `query {
  lengthUnit {
    label
    name
    value
  }
}`

const queryGetWeightUnits = `query {
  weightUnit {
    label
    name
    value
  }
}`

const queryGetStockMovements = `query($page: Int, $first: Int!, $value: Mixed) {
  stockMovements(page: $page, first: $first, where: 
    {
        HAS: {
          relation: "items",
          condition: {
            column: "ID",
            operator: EQ,
            value: $value
          }
      }
    },
      orderBy: [
        {
          column: MOVEMENT_DATE,
          order: DESC
        }
      ]
) {
    data {
      id
      fulfillmentNumber
      type
      movementDate
      deliveryMethod
      orderId
      remarks
      warehouse {
        id
        name
      }
      items {
        id
        productName
        stockMovementPivot {
          quantity
        }
      }
      creator {
        id
        name
      }
      createdAt
    }
    paginatorInfo {
      currentPage
      total
    }
  }
}`

// ========== GraphQL Mutations ==========

const mutationCreateInventory = `mutation(
  $input: CreateInventoryInput!
) {
  inventoryCreate(
    input: $input
  ) {
    id
  }
}`

const mutationUpdateInventory = `mutation(
  $input: UpdateInventoryInput!
) {
  inventoryUpdate(
    input: $input
  ) {
    id
  }
}`

const mutationDeleteInventory = `mutation($id: ID!) {
  inventoryDelete(id: $id) {id}
}`

const mutationStockMovementCreate = `mutation(
  $input: CreateStockMovementInput!
) {
  stockMovementCreate(
    input: $input
  ) {
    id
  }
}`

// ========== Service Functions ==========

export const getInventories = async ({ page, limit, productName, warehouseId }) => {
  try {
    const wId = Number(warehouseId)
    const response = await http.post(`/graphql`, {
      query: queryGetInventories(wId),
      variables: {
        page,
        first: limit,
        value: `%${productName}%`,
        warehouseId: wId
      },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getInventory = async (id) => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetInventory,
      variables: { id: Number(id) }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getWarehouses = async () => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetWarehouses,
      variables: {
        first: 10
      }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getMainCategories = async () => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetMainCategories,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getChildCategories = async (parentId) => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetChildCategories,
      variables: { value: Number(parentId) }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getUsers = async (searchName) => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetUsers,
      variables: { value: `%${searchName}%` }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getProducts = async (searchTitle) => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetProducts,
      variables: { value: `%${searchTitle}%` }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getQuantityUnits = async () => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetQuantityUnits,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getDimensionUnits = async () => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetDimensionUnits,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getWeightUnits = async () => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetWeightUnits,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getStockMovements = async ({ page, limit, inventoryId }) => {
  try {
    const response = await http.post(`/graphql`, {
      query: queryGetStockMovements,
      variables: {
        page,
        first: limit,
        value: Number(inventoryId),
      },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createInventory = async (payload) => {
  try {
    const response = await http.post(`/graphql`, {
      query: mutationCreateInventory,
      variables: { input: payload }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const updateInventory = async (payload) => {
  try {
    const response = await http.post(`/graphql`, {
      query: mutationUpdateInventory,
      variables: { input: payload }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const deleteInventoryItem = async (id) => {
  try {
    const response = await http.post(`/graphql`, {
      query: mutationDeleteInventory,
      variables: { id: Number(id) }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createStockMovement = async (payload) => {
  try {
    const response = await http.post(`/graphql`, {
      query: mutationStockMovementCreate,
      variables: { input: payload }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const searchCouriers = async (search) => {
  try {
    const params = {
      'page[num]': 1,
      'page[limit]': 20,
      'filter[name][like]': search,
    }
    const response = await http.get(`api/v2/couriers`, { params })
    return response.data
  } catch (error) {
    throw error
  }
}

export const searchOrders = async (search) => {
  try {
    const params = {
      'page[num]': 1,
      'page[limit]': 20,
      'fields[order]': 'id,order_code',
      'filter[order_code][like]': search,
    }
    const response = await http.get(`api/v2/orders/admin`, { params })
    return response.data
  } catch (error) {
    throw error
  }
}
